import {
  Me,
  PreviewPanel,
  PreviewPeople,
  PreviewStatus,
  SignInOptions,
  SignOutResponse,
  type StartPreview,
} from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import { setOrganizationFiscalStart } from '../ui/fiscal';
import { setOrganizationTimeZone } from '../ui/format';
import { request } from './client';

/** The signed-in person: identity, roles, Level 1 items and preferences (`GET /api/v1/me`). */
export const meQuery = queryOptions({
  queryKey: ['me'] as const,
  queryFn: async ({ signal }) => {
    const me = await request('/api/v1/me', { schema: Me, signal });
    // Every date and time on screen is shown in this zone (APP-018).
    setOrganizationTimeZone(me.organization.timeZone);
    // And every fiscal year from this start (APP-018).
    if (me.organization.fiscalYear !== undefined)
      setOrganizationFiscalStart(me.organization.fiscalYear.start);
    return me;
  },
  staleTime: 5 * 60_000,
  // A 401 must reach the route loader at once: the loader redirects to sign-in itself.
  retry: false,
  meta: { handlesNotSignedIn: true },
});

/** Which sign-in splash to show (`GET /auth/sign-in-options`, public). */
export const signInOptionsQuery = queryOptions({
  queryKey: ['sign-in-options'] as const,
  queryFn: ({ signal }) => request('/auth/sign-in-options', { schema: SignInOptions, signal }),
  staleTime: Infinity,
  retry: false,
});

/** The splash when the options can't be read: B, the photo wall (the seeded default). */
export const FALLBACK_SIGN_IN_OPTIONS: SignInOptions = { splash: 'photo_wall' };

/**
 * Ends the MyRewards session (`POST /auth/sign-out`). The answer says where to go next: the
 * Microsoft Entra ID sign-out page, or `/sign-in?signedOut=1` when the IdP can't be reached.
 */
export function signOut(): Promise<SignOutResponse> {
  return request('/auth/sign-out', { method: 'POST', schema: SignOutResponse });
}

/**
 * What the Preview as panel opens with (`GET /api/v1/me/preview`): whether you may preview, what
 * you're previewing now and the sectors a Sector Lead or Delegate can be scoped to.
 */
export const previewPanelQuery = queryOptions({
  queryKey: ['me', 'preview'] as const,
  queryFn: ({ signal }) => request('/api/v1/me/preview', { schema: PreviewPanel, signal }),
  staleTime: 5 * 60_000,
});

/**
 * APP-122 (dev and test only): anyone active to act as, found by name or work email
 * (`GET /api/v1/me/preview/people`). Two characters at least.
 */
export function previewPeopleQuery(q: string) {
  return queryOptions({
    queryKey: ['me', 'preview', 'people', q] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/me/preview/people?q=${encodeURIComponent(q)}`, {
        schema: PreviewPeople,
        signal,
      }),
    staleTime: 60_000,
  });
}

/** Starts Preview as, or changes the combination (`POST /api/v1/me/preview`). Audited. */
export function startPreview(input: StartPreview): Promise<PreviewStatus> {
  return request('/api/v1/me/preview', { method: 'POST', body: input, schema: PreviewStatus });
}

/** Ends Preview as (`DELETE /api/v1/me/preview`). The server never refuses an exit. */
export function exitPreview(): Promise<PreviewStatus> {
  return request('/api/v1/me/preview', { method: 'DELETE', schema: PreviewStatus });
}
